import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Loader2,
  Mail,
  CheckCircle,
  XCircle,
  ExternalLink,
  Unlink,
  Settings as SettingsIcon,
  User,
  Building2,
  Lock,
  Eye,
  EyeOff,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';

const Settings = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [gmailStatus, setGmailStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  
  // Change password state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Deal submission email state
  const [dealSubmissionSettings, setDealSubmissionSettings] = useState({
    submission_email: '',
    submission_email_enabled: false,
  });
  const [savingDealSettings, setSavingDealSettings] = useState(false);

  useEffect(() => {
    fetchGmailStatus();
    if (user?.role === 'Admin') {
      fetchDealSubmissionSettings();
    }
    
    // Handle OAuth callback messages
    const gmailConnected = searchParams.get('gmail_connected');
    const gmailError = searchParams.get('gmail_error');
    
    if (gmailConnected === 'true') {
      toast.success('Gmail connected successfully!');
      // Clean URL
      window.history.replaceState({}, '', '/settings');
    } else if (gmailError) {
      toast.error(`Gmail connection failed: ${gmailError}`);
      window.history.replaceState({}, '', '/settings');
    }
  }, [searchParams, user?.role]);

  const fetchGmailStatus = async () => {
    try {
      const response = await api.get('/oauth/gmail/status');
      setGmailStatus(response.data);
    } catch (error) {
      // Gmail status fetch failed - will show disconnected state
    } finally {
      setLoading(false);
    }
  };

  const fetchDealSubmissionSettings = async () => {
    try {
      const response = await api.get('/settings/deal-submission');
      setDealSubmissionSettings(response.data);
    } catch (error) {
      // Settings fetch failed - will use default values
    }
  };

  const handleSaveDealSubmissionSettings = async () => {
    setSavingDealSettings(true);
    try {
      await api.post('/settings/deal-submission', dealSubmissionSettings);
      toast.success('Deal submission settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingDealSettings(false);
    }
  };

  const handleConnectGmail = async () => {
    setConnecting(true);
    try {
      const response = await api.get('/oauth/gmail/connect');
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start Gmail connection');
      setConnecting(false);
    }
  };

  const handleDisconnectGmail = async () => {
    if (!window.confirm('Are you sure you want to disconnect Gmail? You will no longer be able to send emails from the CRM.')) {
      return;
    }
    
    setDisconnecting(true);
    try {
      await api.delete('/oauth/gmail/disconnect');
      setGmailStatus({ connected: false });
      toast.success('Gmail disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Gmail');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      toast.success('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
          Settings
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Manage your account and integrations
        </p>
      </div>

      {/* Profile Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-emerald-500" />
            Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-emerald-700">
                  {user?.name?.charAt(0)?.toUpperCase() || '?'}
                </span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {user?.name}
                </h3>
                <p className="text-slate-500">{user?.email}</p>
                <Badge className="mt-1">{user?.role}</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gmail Integration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-red-500" />
            Gmail Integration
          </CardTitle>
          <CardDescription>
            Connect your Gmail account to send and receive emails directly from the CRM
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : gmailStatus?.connected ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-500" />
                  <div>
                    <p className="font-medium text-emerald-800 dark:text-emerald-200">
                      Gmail Connected
                    </p>
                    <p className="text-sm text-emerald-600 dark:text-emerald-400">
                      {gmailStatus.email}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDisconnectGmail}
                  disabled={disconnecting}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="disconnect-gmail-btn"
                >
                  {disconnecting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Unlink className="w-4 h-4 mr-2" />
                      Disconnect
                    </>
                  )}
                </Button>
              </div>
              <p className="text-sm text-slate-500">
                You can now send emails to leads directly from their detail page.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <XCircle className="w-6 h-6 text-slate-400" />
                  <div>
                    <p className="font-medium text-slate-700 dark:text-slate-300">
                      Gmail Not Connected
                    </p>
                    <p className="text-sm text-slate-500">
                      Connect your Gmail to send emails from the CRM
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleConnectGmail}
                  disabled={connecting}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  data-testid="connect-gmail-btn"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Connect Gmail
                    </>
                  )}
                </Button>
              </div>
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                  What you can do with Gmail integration:
                </h4>
                <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                  <li>• Send emails to leads directly from lead detail pages</li>
                  <li>• View your recent emails in the CRM</li>
                  <li>• Track email activity in lead history</li>
                  <li>• Compose and send personalized messages</li>
                </ul>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-amber-500" />
            Change Password
          </CardTitle>
          <CardDescription>
            Update your account password
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <div className="relative">
                <Input
                  id="current_password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  placeholder="Enter current password"
                  required
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  placeholder="Enter new password (min 8 characters)"
                  required
                  minLength={8}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                placeholder="Confirm new password"
                required
                data-testid="confirm-new-password-input"
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={changingPassword}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="change-password-btn"
            >
              {changingPassword ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Changing...
                </>
              ) : (
                'Change Password'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Deal Submission Email (for Admins) */}
      {user?.role === 'Admin' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-500" />
              Deal Submission Email
            </CardTitle>
            <CardDescription>
              Configure the email address where deals will be sent for submission/processing
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="submission_enabled" className="font-medium">Enable Deal Submissions</Label>
                <p className="text-sm text-slate-500">Allow users to submit deals via email</p>
              </div>
              <Switch
                id="submission_enabled"
                checked={dealSubmissionSettings.submission_email_enabled}
                onCheckedChange={(checked) => setDealSubmissionSettings({
                  ...dealSubmissionSettings,
                  submission_email_enabled: checked
                })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="submission_email">Submission Email Address</Label>
              <Input
                id="submission_email"
                type="email"
                value={dealSubmissionSettings.submission_email}
                onChange={(e) => setDealSubmissionSettings({
                  ...dealSubmissionSettings,
                  submission_email: e.target.value
                })}
                placeholder="deals@yourcompany.com"
                data-testid="submission-email-input"
              />
              <p className="text-sm text-slate-500">
                When a user clicks &quot;Submit Deal&quot; on a lead, an email with all lead details and attachments will be sent to this address.
              </p>
            </div>
            
            <Button
              onClick={handleSaveDealSubmissionSettings}
              disabled={savingDealSettings}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {savingDealSettings ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Organization Info (for Admins) */}
      {user?.role === 'Admin' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-500" />
              Organization
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600 dark:text-slate-400">
              Organization settings can be managed from the Platform Dashboard.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Settings;
